import {
    Avatar,
    IconButton,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
    ListItemSecondaryAction,
    Box,
} from "@mui/material";
import React, { useCallback, useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { IFollowUser, IFollowersData } from "../types/socialEdges";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import axiosService from "../utils/axios";

interface FollowRowProps {
    follower: IFollowUser;
    mode: "pending" | "approved";
    tabType: "Followers" | "Followings";
    update?: () => Promise<void>;
    setFunction?: Dispatch<SetStateAction<IFollowersData | null>>;
    isProfileOwner?: boolean;
}

const FollowRow: React.FC<FollowRowProps> = ({ follower, mode, tabType, update, setFunction, isProfileOwner }) => {
    const [busy, setBusy] = React.useState<null | "approve" | "reject" | "remove">(null);

    const apiUrlBase = `${process.env.REACT_APP_API_URL}/socials`;
    const apiTabTypeUrl = tabType === "Followers" ? "/follower" : "/following";

    const onReject = useCallback(() => {
        if (setFunction)
            setFunction((prev) =>
                prev
                    ? {
                          pending: prev.pending.filter((p) => p._id !== follower._id),
                          approved: prev.approved,
                      }
                    : prev
            );

        axiosService.post(`${apiUrlBase}${apiTabTypeUrl}/deny`, { requestId: follower._id }).then((res) => {
            if (update) update();
        });
    }, [setFunction, apiUrlBase, apiTabTypeUrl, follower._id, update]);

    const onApprove = useCallback(() => {
        if (setFunction)
            setFunction((prev) =>
                prev
                    ? {
                          pending: prev.pending.filter((p) => p._id !== follower._id),
                          approved: prev.approved.concat({ ...follower, status: "approved" }),
                      }
                    : prev
            );

        axiosService.post(`${apiUrlBase}${apiTabTypeUrl}/approve`, { requestId: follower._id }).then((res) => {
            if (update) update();
        });
    }, [setFunction, apiUrlBase, apiTabTypeUrl, follower, update]);

    const onRemove = useCallback(() => {
        if (setFunction)
            setFunction((prev) =>
                prev
                    ? {
                          pending: prev.pending,
                          approved: prev.approved.filter((a) => a._id !== follower._id),
                      }
                    : prev
            );

        axiosService.post(`${apiUrlBase}${apiTabTypeUrl}/remove`, { requestId: follower._id }).then(() => {
            if (update) update();
        });
    }, [setFunction, apiUrlBase, apiTabTypeUrl, follower._id, update]);

    const renderListItemSecondaryAction = useMemo(() => {
        const wrap = (kind: "approve" | "reject" | "remove", fn?: (u: string) => any) => async () => {
            if (!fn) return;
            try {
                setBusy(kind);
                await fn(follower.username);
            } finally {
                setBusy(null);
            }
        };

        if (isProfileOwner) {
            if (mode === "pending") {
                return (
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Approve">
                            <span>
                                <IconButton
                                    edge="end"
                                    aria-label="approve"
                                    onClick={wrap("approve", onApprove)}
                                    disabled={!onApprove || busy !== null}
                                    size="small"
                                >
                                    <CheckIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Reject">
                            <span>
                                <IconButton
                                    edge="end"
                                    aria-label="reject"
                                    onClick={wrap("reject", onReject)}
                                    disabled={!onReject || busy !== null}
                                    size="small"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                );
            } else {
                return (
                    <Tooltip title={"Remove " + tabType}>
                        <span>
                            <IconButton
                                edge="end"
                                aria-label="remove"
                                onClick={wrap("remove", onRemove)}
                                disabled={!onRemove || busy !== null}
                                size="small"
                            >
                                <PersonRemoveIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            }
        } else return <Box />;
    }, [isProfileOwner, follower.username, mode, onApprove, busy, onReject, tabType, onRemove]);

    return (
        <ListItem sx={{ py: 1.25 }} secondaryAction={<ListItemSecondaryAction>{renderListItemSecondaryAction}</ListItemSecondaryAction>}>
            <ListItemAvatar>
                <Avatar src={follower.image ?? undefined} alt={follower.username} imgProps={{ referrerPolicy: "no-referrer" }}>
                    {initials(follower.username)}
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                primary={
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        component="a"
                        href={`/profile/${follower.username}`}
                        sx={{ userSelect: "none", textDecoration: "none" }}
                    >
                        <Typography sx={{ cursor: "pointer", fontWeight: 600 }}>{follower.username}</Typography>
                    </Stack>
                }
                secondary={
                    follower.status && (
                        <Typography variant="body2" color="text.secondary">
                            {follower.createdAt}
                        </Typography>
                    )
                }
            />
        </ListItem>
    );
};

const initials = (name: string) => {
    const parts = name.split(/[._\s-]+/).filter(Boolean);
    const first = parts[0]?.[0] ?? "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
    return (first + last).toUpperCase();
};

export default FollowRow;
